import { useCallback, useEffect, useRef, useState } from "react";
import { AskEvePanelContent, type AskEvePanelVariant } from "@/components/application/ai-chat/ask-eve-panel-content";

/** Larghezza minima del pannello: almeno ~33vw (leggibilità), con floor per viewport piccole. */
const MIN_WIDTH_VW = 0.33;
const MIN_WIDTH_FLOOR_PX = 300;
const STORAGE_KEY = "evemind_ask_eve_panel_width_px";

function clamp(n: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, n));
}

function readStoredWidth(): number | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const n = Number.parseInt(raw, 10);
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
}

function getMinWidth(): number {
    if (typeof window === "undefined") return 520;
    return Math.max(MIN_WIDTH_FLOOR_PX, Math.round(window.innerWidth * MIN_WIDTH_VW));
}

/** Larghezza approssimativa della colonna contenuto (stesso offset sidebar usato per il max). */
function getContentApproxWidth(): number {
    if (typeof window === "undefined") return 900;
    return Math.max(280, window.innerWidth - 280);
}

/** Default: al massimo metà della larghezza contenuto, non metà viewport. */
function getDefaultWidth(): number {
    if (typeof window === "undefined") return 450;
    return Math.round(getContentApproxWidth() * 0.5);
}

function getMaxWidth(): number {
    if (typeof window === "undefined") return 2000;
    const candidate = Math.min(Math.round(window.innerWidth * 0.9), getContentApproxWidth());
    return Math.max(candidate, getMinWidth());
}

type AskEveSidePanelProps = {
    variant: AskEvePanelVariant;
    onClose: () => void;
};

/**
 * Pannello destro sopra al contenuto (overlay), ridimensionabile con handle sul bordo sinistro.
 * Deve essere reso dentro un contenitore `relative` che avvolge l’area main.
 */
export function AskEveSidePanel({ variant, onClose }: AskEveSidePanelProps) {
    const [panelWidth, setPanelWidth] = useState(() => {
        const stored = readStoredWidth();
        const min = getMinWidth();
        const max = getMaxWidth();
        if (stored != null) return clamp(stored, min, max);
        return clamp(getDefaultWidth(), min, max);
    });

    const [isResizing, setIsResizing] = useState(false);
    const resizeRef = useRef<{ startX: number; startW: number } | null>(null);

    useEffect(() => {
        const onResize = () => {
            setPanelWidth((w) => clamp(w, getMinWidth(), getMaxWidth()));
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, String(panelWidth));
        } catch {
            /* ignore */
        }
    }, [panelWidth]);

    useEffect(() => {
        if (!isResizing) return;

        const onMove = (e: PointerEvent) => {
            if (!resizeRef.current) return;
            const delta = resizeRef.current.startX - e.clientX;
            const next = clamp(resizeRef.current.startW + delta, getMinWidth(), getMaxWidth());
            setPanelWidth(next);
        };

        const end = () => {
            resizeRef.current = null;
            setIsResizing(false);
        };

        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        document.addEventListener("pointermove", onMove);
        document.addEventListener("pointerup", end);
        document.addEventListener("pointercancel", end);

        return () => {
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
            document.removeEventListener("pointermove", onMove);
            document.removeEventListener("pointerup", end);
            document.removeEventListener("pointercancel", end);
        };
    }, [isResizing]);

    const onResizePointerDown = useCallback(
        (e: React.PointerEvent) => {
            e.preventDefault();
            e.stopPropagation();
            resizeRef.current = { startX: e.clientX, startW: panelWidth };
            setIsResizing(true);
        },
        [panelWidth]
    );

    const widthPx = clamp(panelWidth, getMinWidth(), getMaxWidth());

    return (
        <aside
            className="absolute inset-y-0 right-0 z-50 flex min-h-0 min-w-0 border-l border-secondary bg-primary shadow-2xl ring-1 ring-secondary"
            style={{ width: widthPx }}
        >
            <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize Ask Eve panel"
                className="group relative w-3 shrink-0 cursor-col-resize touch-none bg-secondary hover:bg-tertiary"
                onPointerDown={onResizePointerDown}
            >
                <span className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-tertiary opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <AskEvePanelContent variant={variant} showClose onClose={onClose} />
            </div>
        </aside>
    );
}
